import { Box, Button, Stack, Typography } from '@mui/material'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import {
    formatFloorArea,
    propertyKindLabel
} from '@/features/dashboard/admin/locationDetails'
import { houseDetailsPath } from '@/features/dashboard/power/houseStandings'
import CategoryBreakdownBar from '@/features/dashboard/power/meter-categories/CategoryBreakdownBar'
import usePropertyCategoryBreakdown from '@/features/dashboard/power/meter-categories/usePropertyCategoryBreakdown'
import { isFeatureFlagEnabled } from '@/features/dashboard/site-admin/orgFeatureFlags'
import useAuth from '@/hooks/useAuth'
import useTerminology from '@/hooks/useTerminology'
import { describeLastReading } from '@/lib/freshnessCopy'
import { ADMIN_ROLES, SLT_ROLES } from '@/lib/roles'
import { hasRole } from '@/utils/hasRole'
import EntityLink from '../EntityLink'
import { childLocations, metersAtLocation } from '../relations'
import MeterRows from './MeterRows'
import PeekLayout, {
    PeekDetails,
    PeekPlaceholder,
    PeekSection
} from './PeekLayout'
import UsageSummary from './UsageSummary'
import { hasUsage } from './useDailyUsage'
import useRelatedRecords from './useRelatedRecords'

const USAGE_FUELS = [
    ['electricity-meter', 'electricity', 'Electricity'],
    ['gas-meter', 'gas', 'Gas']
]

/**
 * Whether House Details can show this location's use: the reader can open
 * that page (its route's roles and feature flag) and the location has a
 * meter of the fuel the page opens on — electricity, or gas for a gas-only
 * organisation. Otherwise the page would bounce them to another house.
 */
function canViewUsage(user, activeOrg, meters) {
    const fuel =
        activeOrg?.has_electricity === false && activeOrg?.has_gas
            ? 'gas-meter'
            : 'electricity-meter'
    return (
        Boolean(user && hasRole(user, SLT_ROLES)) &&
        isFeatureFlagEnabled(activeOrg, 'house_details') &&
        meters.some((entry) => entry.type === fuel)
    )
}

/**
 * The property facts recorded for a location, set ones only: a missing value
 * is left out rather than shown as "0 m²" or a dash. Readers without the
 * admin-only fields (kind, EPC) simply don't receive them from the API.
 */
function propertyRows(location, peopleTerm) {
    const expiry = location.epcExpiry
        ? DateTime.fromISO(location.epcExpiry).toLocaleString(DateTime.DATE_MED)
        : null
    return [
        formatFloorArea(location.floorAreaM2) && {
            label: 'Floor area',
            value: formatFloorArea(location.floorAreaM2)
        },
        location.occupancy != null && {
            label: `Number of ${peopleTerm}`,
            value: String(location.occupancy)
        },
        location.bedrooms != null && {
            label: 'Bedrooms',
            value: String(location.bedrooms)
        },
        propertyKindLabel(location.propertyKind) && {
            label: 'Kind',
            value: propertyKindLabel(location.propertyKind)
        },
        location.epcRating && {
            label: 'EPC rating',
            value: location.epcRating
        },
        expiry && { label: 'EPC expires', value: expiry }
    ].filter(Boolean)
}

/**
 * Whether to fetch this location's last-30-days category breakdown (openspec:
 * meter-categories): only for org admins (the portfolio endpoint's audience) and only
 * when the location has a categorised electricity meter, so an uncategorised org never
 * makes the request and sees no new section.
 */
function wantsCategoryBreakdown(user, meters) {
    return (
        Boolean(user && hasRole(user, ADMIN_ROLES)) &&
        meters.some(
            (entry) =>
                entry.type === 'electricity-meter' && entry.meter.category
        )
    )
}

/** A location: what it is, the meters in it and what they've used. */
export default function LocationPeek({ id }) {
    const { user, activeOrg } = useAuth()
    const { t } = useTerminology()
    const peopleTerm = t('pupils')
    const records = useRelatedRecords()
    const location = records.locations.find(
        (candidate) => String(candidate.id) === String(id)
    )
    const breakdownWanted = location
        ? wantsCategoryBreakdown(user, metersAtLocation(location.id, records))
        : false
    const { breakdown } = usePropertyCategoryBreakdown(location?.id, {
        enabled: breakdownWanted
    })

    if (!location) {
        return <PeekPlaceholder kind="Location" loading={records.isLoading} />
    }

    const meters = metersAtLocation(location.id, records)
    const children = childLocations(records.locations, location.id)
    const facts = propertyRows(location, peopleTerm)

    return (
        <PeekLayout
            kind="Location"
            title={
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    component="span"
                >
                    {location.color ? (
                        <Box
                            component="span"
                            aria-hidden="true"
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: location.color,
                                flexShrink: 0
                            }}
                        />
                    ) : null}
                    <span>{location.isamsName}</span>
                </Stack>
            }
            subtitle={[
                location.isamsId ? `Ref ${location.isamsId}` : null,
                location.locationType?.name
            ]
                .filter(Boolean)
                .join(' · ')}
            actions={
                <>
                    {canViewUsage(user, activeOrg, meters) ? (
                        <Button
                            component={RouterLink}
                            to={houseDetailsPath(location.id)}
                            size="small"
                            variant="outlined"
                        >
                            View usage
                        </Button>
                    ) : null}
                    <Button
                        component={RouterLink}
                        to="/admin/locations"
                        size="small"
                        variant="outlined"
                    >
                        All locations
                    </Button>
                </>
            }
        >
            {USAGE_FUELS.map(([type, fuel, label]) => {
                const ids = meters
                    .filter((entry) => entry.type === type)
                    .map((entry) => entry.meter.id)
                return ids.length > 0 && hasUsage(fuel) ? (
                    <UsageSummary
                        key={fuel}
                        fuel={fuel}
                        meterIds={ids}
                        label={label}
                    />
                ) : null
            })}

            {breakdownWanted && breakdown ? (
                <PeekSection title="Electricity by category · last 30 days">
                    <CategoryBreakdownBar
                        breakdown={breakdown}
                        wiringHref="/admin/meters"
                    />
                </PeekSection>
            ) : null}

            <PeekSection title={`Meters (${meters.length})`}>
                {meters.length > 0 ? (
                    <MeterRows entries={meters} />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No meters are assigned here yet. Assign one from its
                        edit page on{' '}
                        <RouterLink to="/admin/meters">Meters</RouterLink>.
                    </Typography>
                )}
            </PeekSection>

            {facts.length > 0 ? (
                <PeekSection title="Property">
                    <PeekDetails rows={facts} />
                </PeekSection>
            ) : null}

            <PeekSection title="Details">
                <PeekDetails
                    rows={[
                        {
                            label: 'Type',
                            value: location.locationType?.name ?? '—'
                        },
                        {
                            label: 'Part of',
                            value: location.parent ? (
                                <EntityLink
                                    type="location"
                                    id={location.parent.id}
                                >
                                    {location.parent.isamsName}
                                </EntityLink>
                            ) : (
                                '—'
                            )
                        },
                        children.length > 0 && {
                            label: 'Includes',
                            value: (
                                <Stack component="span" spacing={0.5}>
                                    {children.map((child) => (
                                        <EntityLink
                                            key={child.id}
                                            type="location"
                                            id={child.id}
                                        >
                                            {child.isamsName}
                                        </EntityLink>
                                    ))}
                                </Stack>
                            )
                        },
                        {
                            label: 'Latest data',
                            value: describeLastReading(location.lastReadingAt)
                                .text
                        }
                    ]}
                />
            </PeekSection>
        </PeekLayout>
    )
}

LocationPeek.propTypes = {
    id: PropTypes.string.isRequired
}
